"use client";

import { buildListingUrl } from "@/lib/url";
import type { ListingInfoAttribute } from "@repo/utils";
import Link from "next/link";
import { useEffect, useRef } from "react";
import { MapListingCard } from "./map-listing-card";
import type { MapAreaEntry } from "./search-map-points";

export function MapAreaPopup({
  area,
  isActive,
  horizontal = false,
  boostedAttributes,
  onShowListings,
}: {
  area: MapAreaEntry;
  isActive: boolean;
  horizontal?: boolean;
  boostedAttributes?: ListingInfoAttribute[];
  onShowListings: () => void;
}) {
  if (area.count === 1 && area.listingSlug) {
    const card = (
      <div className={horizontal ? undefined : "w-[300px]"}>
        <MapListingCard
          slug={area.listingSlug}
          enabled={isActive}
          horizontal={horizontal}
          boostedAttributes={boostedAttributes}
        />
      </div>
    );

    if (horizontal) {
      return (
        <Link
          href={buildListingUrl({ slug: area.listingSlug })}
          className="block focus-visible:outline-none"
          prefetch={false}
        >
          {card}
        </Link>
      );
    }

    return (
      <a
        href={buildListingUrl({ slug: area.listingSlug })}
        className="block focus-visible:outline-none"
        target="_blank"
        rel="noreferrer"
      >
        {card}
      </a>
    );
  }

  return (
    <AreaListingsPrompt area={area} onShowListings={onShowListings} />
  );
}

function AreaListingsPrompt({
  area,
  onShowListings,
}: {
  area: MapAreaEntry;
  onShowListings: () => void;
}) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const onShowRef = useRef(onShowListings);
  onShowRef.current = onShowListings;

  useEffect(() => {
    const button = buttonRef.current;
    if (!button) return;
    // Mapbox moves this node into the map popup, so the handler stays on the element.
    const handler = (event: Event) => {
      event.preventDefault();
      event.stopPropagation();
      onShowRef.current();
    };
    button.addEventListener("click", handler);
    return () => button.removeEventListener("click", handler);
  }, []);

  const noun = area.count === 1 ? "listing" : "listings";

  return (
    <div className="w-[260px] space-y-2 p-3">
      <p className="text-sm font-medium text-foreground">
        {area.count.toLocaleString()} {noun} in {area.name} without an exact
        address
      </p>
      <button
        ref={buttonRef}
        type="button"
        className="min-h-11 text-sm font-medium text-emerald-700 underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground"
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          onShowRef.current();
        }}
      >
        Show listings
      </button>
    </div>
  );
}
